import * as React from 'react';

export interface HubspotMeetingProps {
    url: string;
    prefill?: {
        email?: string;
        firstName?: string;
        lastName?: string;
        company?: string;
    };
    /**
     * Additional URL parameters to pass to the meeting embed
     */
    additionalParams?: Record<string, string>;
}

export interface HubspotEventListenerProps {
    onEventScheduled?: () => void;
}

/**
 * HubspotInlineWidget component for embedding HubSpot meeting scheduler
 */
export const HubspotInlineWidget: React.FC<HubspotMeetingProps> = ({
    url,
    prefill = {},
    additionalParams = {},
}) => {
    // Build the URL with prefill parameters
    const buildUrl = () => {
        const baseUrl = url;
        const params = new URLSearchParams();

        // Add prefill parameters if they exist
        if (prefill.email) {
            params.append('email', prefill.email);
        }
        if (prefill.firstName) {
            params.append('firstName', prefill.firstName);
        }
        if (prefill.lastName) {
            params.append('lastName', prefill.lastName);
        }
        if (prefill.company) {
            params.append('company', prefill.company);
        }

        // Add embed parameter
        params.append('embed', '1');

        // Add any additional parameters
        if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
                params.append(key, value);
            });
        }

        // Combine URL and parameters
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}${params.toString()}`;
    };

    return (
        <iframe
            src={buildUrl()}
            frameBorder="0"
            title="Schedule Meeting"
        />
    );
};

/**
 * HubspotEventListener component for handling HubSpot meeting events
 */
export const HubspotEventListener: React.FC<React.PropsWithChildren<HubspotEventListenerProps>> = ({
    onEventScheduled,
    children,
}) => {
    React.useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // HubSpot sends a message when a meeting is scheduled
            if (event.data.meetingBookSucceeded) {
                onEventScheduled && onEventScheduled();
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [onEventScheduled]);

    return <>{children}</>;
};
